import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable, of } from "rxjs";
import { map } from "rxjs/operators";
// Import environment config file.
import { environment } from "src/environments/environment";
import { AjaxResponseInterface, ICreateApiResponse, IUpdateApiResponse } from "src/app/interface/global/AjaxResponseInterface";
import UnitType from "src/app/controller/unit-type/models/unit-type";
import Tracker from "src/app/controller/manage-tracker/models/tracker";
import TrackerApiModel, { ITrackerQuery } from "src/app/controller/manage-tracker/models/tracker-api-model";
import TrackerModelsFactory from "src/app/controller/manage-tracker/factories/tracker-models-factory";
import Formula, { IFormula } from "src/app/controller/manage-tracker/models/formula";
import TrackerTag from "src/app/controller/manage-tracker/models/tracker-tag";
import Unit, { IUnit } from "src/app/controller/unit/models.ts/unit";

@Injectable({
  providedIn: "root",
})
export class TrackerService {
  private unitBaseUrl: string = environment.apiUrl + "/unit";
  private unitTypeBaseUrl: string = environment.apiUrl + "/unit-type";
  private manageActivitiesBaseUrl: string = environment.apiUrl + "/activities";
  private trackerTypeBaseUrl: string = environment.apiUrl + "/tracker-type";
  private trackerTagBaseUrl: string = environment.apiUrl + "/tracker-tag";
  private trackerBaseUrl: string = environment.apiUrl + "/tracker";
  private formulaBaseUrl: string = environment.apiUrl + "/formula";
  private manageFoodBaseUrl: string = environment.apiUrl + "/food";
  private nutritionBaseUrl: string =
    environment.apiUrl + "/nutrition-definition/nutrition-list";

  constructor(private http: HttpClient) { }

  /**
   * This is used to get tracker background Img
   */
  public getTrackerBackImg(): string[] {
    const trackerImg = [];
    for (let i = 1; i <= 60; i++) {
      trackerImg.push("image-" + i + ".jpg");
    }
    return trackerImg;
  }

  /**
   * this function is used to create unit record data.
   * @param queryObj: any
   */
  createUnit(bodyParams: any): Observable<any> {
    return this.http.post<any>(this.unitBaseUrl, bodyParams).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  /**
   * this function is used to create unit record data.
   * @param queryObj: any
   */
  createUnitType(bodyParams: any): Observable<any> {
    return this.http.post<any>(this.unitTypeBaseUrl, bodyParams).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  /**
   * this store service is used to update coupon record data.
   * @param queryObj: any
   */
  updateUnitType(queryObj: string, bodyParams: any): Observable<any> {
    return this.http
      .post<any>(
        this.unitTypeBaseUrl + "/update-unit-type?_id=" + queryObj,
        bodyParams
      )
      .pipe(
        map((data: any) => {
          return data;
        })
      );
  }

  isUnitTypeNotAssignedToAnyTrackerOrUnit(unitTypeId:string): Observable<AjaxResponseInterface<string>> {
    return this.http.get<AjaxResponseInterface<string>>(this.unitTypeBaseUrl + "/does-unit-type-not-exist-in-trackers-or-unit?_id=" + unitTypeId);
  }

  deleteUnitType(queryObj: string): Observable<any> {
    // console.log(queryObj);
    const queryParams = {
      params: new HttpParams().set("_id", queryObj),
    };
    return this.http.delete<any>(
      `${this.unitTypeBaseUrl}/delete-unit-type`,
      queryParams
    );
  }

  /**
   * This function is used to get all store according to pagination.
   */
  getUnitTypeList(
    qObj: any
  ): Observable<AjaxResponseInterface<UnitType[]>> {
    return this.http.get<AjaxResponseInterface<UnitType[]>>(
      this.unitTypeBaseUrl,
      { params: qObj }
    );
  }

  /**
   * This function is used to get counts of unit
   */
  getUnitTypeListCounts(qObj: any): Observable<any> {
    return this.http.get<any[]>(this.unitTypeBaseUrl + "/counts", {
      params: qObj,
    });
  }

  /**
   * This function is used to get counts of unit
   */
  getUnitListCounts(qObj: any): Observable<any> {
    return this.http.get<any[]>(this.unitBaseUrl + "/counts", { params: qObj });
  }

  /**
   * this service is used to update unit  data.
   * @param queryObj: any
   */
  updateUnit(queryObj: string, bodyParams: any): Observable<any> {
    return this.http
      .post<any>(this.unitBaseUrl + "/update-unit?_id=" + queryObj, bodyParams)
      .pipe(
        map((data: any) => {
          return data;
        })
      );
  }


  isTrackerNotAssignedToAnyFormulaOrChallenge(trackerId:string): Observable<AjaxResponseInterface<boolean>> {
    return this.http.get<AjaxResponseInterface<boolean>>(this.trackerBaseUrl + "/does-tracker-not-exist-in-formula-or-challenge?_id=" + trackerId);
  }

  isFormulaNotAssignedToAnyTracker(formulaId:string): Observable<AjaxResponseInterface<boolean>> {
    return this.http.get<AjaxResponseInterface<boolean>>(this.trackerBaseUrl + "/does-formula-not-exist-in-tracker?_id=" + formulaId);
  }

  /**
   * This function is used to get all unit list according to pagination
   */
  getUnitList(qObj: any): Observable<AjaxResponseInterface<[]>> {
    return this.http.get<AjaxResponseInterface<[]>>(this.unitBaseUrl, {
      params: qObj,
    });
  }

  getSiUnitOfUnitType(qObj:any):Observable<AjaxResponseInterface<Unit>>{
    return this.http.get<AjaxResponseInterface<Unit>>(this.unitBaseUrl + "/get-siUnit-of-unitType", { params: qObj })
  }

  /**
   * this function is used to create Manage Activities record data.
   * @param queryObj: any
   */
  createManageActivities(bodyParams: any): Observable<any> {
    return this.http.post<any>(this.manageActivitiesBaseUrl, bodyParams).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  /**
   * this store service is used to update Manage Activities record data.
   * @param queryObj: any
   */
  updateManageActivities(queryObj: string, bodyParams: any): Observable<any> {
    return this.http
      .post<any>(
        this.manageActivitiesBaseUrl + "/update-activity?_id=" + queryObj,
        bodyParams
      )
      .pipe(
        map((data: any) => {
          return data;
        })
      );
  }

  /**
   * This function is used to get all Manage Activities according to pagination.
   */
  getManageActivitiesList(qObj: any): Observable<AjaxResponseInterface<[]>> {
    return this.http.get<AjaxResponseInterface<[]>>(
      this.manageActivitiesBaseUrl,
      { params: qObj }
    );
  }

  /**
   * This function is used to get counts of Manage Activities
   */
  getManageActivitiesCounts(qObj: any): Observable<any> {
    return this.http.get<any[]>(this.manageActivitiesBaseUrl + "/count", {
      params: qObj,
    });
  }

  /**
   * this function is used to create tracker type data.
   * @param queryObj: any
   */
  createTrackerType(bodyParams: any): Observable<any> {
    return this.http.post<any>(this.trackerTypeBaseUrl, bodyParams).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  isTrackerTypeNotAssignedToAnyTracker(trackerTypeId:string): Observable<AjaxResponseInterface<boolean>> {
    return this.http.get<AjaxResponseInterface<boolean>>(this.trackerTypeBaseUrl + "/does-tracker-type-not-exist-in-tracker?_id=" + trackerTypeId);
  }

  /**
   * this store service is used to update tracker type record data.
   * @param queryObj: any
   */
  updateTrackerType(queryObj: string, bodyParams: any): Observable<any> {
    return this.http
      .post<any>(
        this.trackerTypeBaseUrl + "/update-tracker-type?_id=" + queryObj,
        bodyParams
      )
      .pipe(
        map((data: any) => {
          return data;
        })
      );
  }

  /**
   * This function is used to get all tracker type according to pagination.
   */
  getTrackerTypeList(qObj: any): Observable<AjaxResponseInterface<[]>> {
    return this.http.get<AjaxResponseInterface<[]>>(this.trackerTypeBaseUrl, {
      params: qObj,
    });
  }

  isTrackerTypeNameFieldUnique(qObj: { [key: string]: string}, thisRef: TrackerService = this): Observable<AjaxResponseInterface<string>> {
    return thisRef.http.get<AjaxResponseInterface<string>>(thisRef.trackerTypeBaseUrl + "/is-tracker-type-name-field-unique",
      { params:qObj });
  }

  /**
   * This function is used to get counts of tracker type
   */
  getTrackerTypeCounts(qObj: any): Observable<any> {
    return this.http.get<any[]>(this.trackerTypeBaseUrl + "/count", {
      params: qObj,
    });
  }

  /**
   * This function is used to get all tracker tag according to pagination.
   */
  getTrackerTagList(qObj: any): Observable<any> {
    return this.http.get<any[]>(this.trackerTagBaseUrl, { params: qObj });
  }

  /**
   * This function is used to create tracker tag
   */
   createTrackerTag(trackerTag: Partial<TrackerTag>):  Observable<AjaxResponseInterface<ICreateApiResponse<TrackerTag>>> {
    return this.http.post<AjaxResponseInterface<ICreateApiResponse<TrackerTag>>>(this.trackerTagBaseUrl, trackerTag);
  }

  /**
   * This function is used to get all nutrition list.
   */
  getNutritionList(qObj: any): Observable<any> {
    return this.http.get<any[]>(this.nutritionBaseUrl, { params: qObj });
  }

  /**
   * this function is used to create tracker record data.
   * @param queryObj: any
   */
  createTracker(bodyParams: TrackerApiModel): Observable<AjaxResponseInterface<TrackerApiModel>> {
    return this.http.post<any>(this.trackerBaseUrl, bodyParams);
  }

  createTrackerByTrackerViewModel(trackerViewModel: Partial<Tracker>): Observable<AjaxResponseInterface<TrackerApiModel>> {
    let trackerApiModel = TrackerModelsFactory.getTrackerApiModel(trackerViewModel);
    return this.createTracker(trackerApiModel);
  }

  /**
   * This function is used to get all tracker List for fomula
   */
  getTrackerListForFormula(query: ITrackerQuery = {}): Observable<AjaxResponseInterface<TrackerApiModel[]>> {
    return this.http.post<AjaxResponseInterface<TrackerApiModel[]>>(
      this.trackerBaseUrl + "/tracker-list-for-formula",
      query
    );
  }

  /**
   * This function is used to get all tracker List.
   */
  getTrackerList(qObj: ITrackerQuery): Observable<AjaxResponseInterface<TrackerApiModel[]>> {
    return this.http.post<AjaxResponseInterface<TrackerApiModel[]>>(
      this.trackerBaseUrl + "/admin-tracker-list",
      qObj
    );
  }


/**
   * This function is used to get all tracker List by tracker View Model.
   */
  getTrackerViewModelList(qObj: ITrackerQuery): Observable<AjaxResponseInterface<Tracker[]>> {
    return this.getTrackerList(qObj).pipe(
      map((response: AjaxResponseInterface<TrackerApiModel[] | Tracker[]>) => {
        response.data = (response.data as TrackerApiModel[]).map((tracker: TrackerApiModel) => {
          return TrackerModelsFactory.getTrackerViewModel(tracker)
        });
        return response as AjaxResponseInterface<Tracker[]>;
      })
    )
  }

  /**
   * This function is used to get counts of coupon */

  getTrackerCount(qObj: any): Observable<AjaxResponseInterface<[]>> {
    return this.http.post<AjaxResponseInterface<[]>>(
      this.trackerBaseUrl + "/tracker-counts",
      qObj
    );
  }

  /**
   * this function is used to create formula.
   * @param queryObj: any
   */
  createFormula(bodyParams: any): Observable<any> {
    return this.http.post<any>(this.formulaBaseUrl, bodyParams);
  }

  /**
   * this function is used to create formula.
   * @param queryObj: any
   * @param formulaId: any
   */
  updateFormula(formulaId: any, bodyParams: any): Observable<any> {
    return this.http
      .post<any>(
        this.formulaBaseUrl + "/update-formula?_id=" + formulaId,
        bodyParams
      )
      .pipe(
        map((data: any) => {
          return data;
        })
      );
  }

  isFormulaFieldUnique(params: { [key: string]: string}, thisRef: TrackerService = this): Observable<AjaxResponseInterface<string>> {
    return thisRef.http.get<AjaxResponseInterface<string>>(thisRef.formulaBaseUrl + '/is-formula-field-unique',
      { params });
  }

  /**
   * This function is used to get all unit list according to pagination
   */
  getManageFoodList(qObj: any): Observable<any> {
    return this.http.get<any[]>(this.manageFoodBaseUrl + "/search-food", {
      params: qObj,
    });
  }

  /**
   * This function is used to get counts of tracker type
   */
  getManageFoodCounts(qObj: any): Observable<any> {
    return this.http.get<any[]>(this.manageFoodBaseUrl + "/count", {
      params: qObj,
    });
  }

  /**
   * This function is used to get all formula List.
   */
  getFormulaList(qObj: IFormula): Observable<AjaxResponseInterface<Formula[]>> {
    return this.http.post<AjaxResponseInterface<Formula[]>>(this.formulaBaseUrl + "/formula-list", qObj);
  }

  /**
   * This function is used to get formula count.
   */
   getFormulaCount(qObj: IFormula): Observable<AjaxResponseInterface<number>> {
    return this.http.post<AjaxResponseInterface<number>>(this.formulaBaseUrl + "/formula-count", qObj);
  }

  /*
    this service is used to update tracker data.
    @param queryObj: any

  */
  updateTracker(trackerId: string, bodyParams: Partial<TrackerApiModel>): Observable<AjaxResponseInterface<IUpdateApiResponse>> {
    return this.http
      .post<AjaxResponseInterface<IUpdateApiResponse>>(
        this.trackerBaseUrl + "/admin-tracker-list-update?_id=" + trackerId,
        bodyParams
      );
  }

  updateTrackerByViewModel(trackerId: string, trackerViewModel: Partial<Tracker>): Observable<AjaxResponseInterface<IUpdateApiResponse>> {
    let trackerApiModel = TrackerModelsFactory.getTrackerApiModel(trackerViewModel);
    return this.updateTracker(trackerId, trackerApiModel);
  }

  /**
   * this function is used to create food.
   * @param queryObj: any
   */
  createFood(bodyParams: any): Observable<any> {
    return this.http.post<any>(this.manageFoodBaseUrl, bodyParams).pipe(
      map((data: any) => {
        return data;
      })
    );
  }

  updateFood(queryObj: string, bodyParams: any): Observable<any> {
    return this.http
      .post<any>(
        this.manageFoodBaseUrl + "/update-food?_id=" + queryObj,
        bodyParams
      )
      .pipe(
        map((data: any) => {
          return data;
        })
      );
  }
}
